import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react'
import { FiNavigation } from 'react-icons/fi'
import { actionStopState, type WalkStop } from '../../../lib/walkStops'
import { directionsUrl } from '../../../lib/walkGeo'

/**
 * [CF-1 P3 §7.3] The current action stop: address, the open residents with their door notes,
 * Directions (a plain external link: no share/copy, noExport.guard) and the two outcome buttons.
 * Both buttons are disabled while an outcome is saving (the double-tap guard).
 */
export function ActionStopCard({ stop, travelMode, saving, onDone, onCouldnt }: {
  stop: WalkStop; travelMode: 'walking' | 'driving'; saving: boolean
  onDone: () => void; onCouldnt: () => void
}) {
  const open = stop.residents.filter(r => actionStopState(r) !== 'done')
  const lat = stop.lat
  const lng = stop.lng
  return (
    <Box data-testid="action-stop-card" bg="surface" borderTopWidth="1px" borderColor="border" px={4} pt={3}
      pb="calc(12px + env(safe-area-inset-bottom, 0px))" flexShrink={0}>
      <VStack align="stretch" gap={3}>
        {/* Long notes scroll inside the card so the buttons always stay on screen. */}
        <VStack align="stretch" gap={2} maxH="35vh" overflowY="auto">
          <HStack justify="space-between" align="center" gap={2}>
            <Box minW={0}>
              <Text fontWeight="bold" fontSize="lg" lineClamp={2}>{stop.address}</Text>
              {stop.city && <Text fontSize="sm" color="fg.muted">{stop.city}</Text>}
            </Box>
            {lat != null && lng != null && (
              <Button asChild minH="44px" variant="outline" borderColor="border" flexShrink={0}>
                <a href={directionsUrl(lat, lng, travelMode)} target="_blank" rel="noopener noreferrer">
                  <FiNavigation aria-hidden />
                  Directions
                </a>
              </Button>
            )}
          </HStack>
          {open.map(r => (
            <Box key={r.contactId}>
              <Text fontWeight="medium">{`${r.firstName} ${r.lastName ?? ''}`.trim()}</Text>
              {r.followUpNote && (
                <Box mt={1} pl={3} borderLeftWidth="2px" borderColor="accent">
                  <Text fontSize="xs" fontWeight="bold" color="fg.muted" textTransform="uppercase" letterSpacing="wider">
                    Note from the door
                  </Text>
                  <Text fontSize="sm" whiteSpace="pre-wrap">{r.followUpNote}</Text>
                </Box>
              )}
            </Box>
          ))}
        </VStack>
        <VStack align="stretch" gap={2}>
          <Button w="100%" minH="56px" colorPalette="brand" disabled={saving} onClick={onDone}>Dropped off</Button>
          <Button w="100%" minH="56px" variant="outline" borderColor="border" disabled={saving} onClick={onCouldnt}>Couldn't</Button>
        </VStack>
      </VStack>
    </Box>
  )
}
